import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, Copy, Plus, RefreshCw, AlertCircle } from "lucide-react";
import { frameworkApi } from "@/api/framework";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AdminListPager, AdminListToolbar } from "@/components/admin/AdminListToolbar";
import { useAdminListQuery } from "@/components/admin/useAdminListQuery";

const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-GB", { dateStyle: "medium" })
    : "—";
const currentFor = (versions) =>
  versions
    .filter(
      (v) =>
        v.status === "published" &&
        (!v.effective_from || v.effective_from <= today()),
    )
    .sort(
      (a, b) =>
        String(b.effective_from || "").localeCompare(
          String(a.effective_from || ""),
        ) || b.version - a.version,
    )[0] ?? null;

export default function Framework() {
  const createTrigger = useRef(null);
  const navigate = useNavigate();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [cloneBusy, setCloneBusy] = useState("");
  const [createError, setCreateError] = useState("");
  const [form, setForm] = useState({
    scheme: "disability_confident",
    label: "",
    notes: "",
    effective_from: "",
  });
  const generation = useRef(0);
  const { query, update, reset } = useAdminListQuery();
  const [total, setTotal] = useState(0);

  const load = async () => {
    const token = ++generation.current;
    setLoading(true);
    setError("");
    try {
      const rows = await frameworkApi.list({ paged: "true", limit: "25", ...(query.q ? { q: query.q } : {}), ...(query.status ? { status: query.status } : {}), ...(query.offset ? { offset: query.offset } : {}) });
      if (token === generation.current)
        { setVersions(rows.items || []); setTotal(Number(rows.total) || 0); }
    } catch (e) {
      if (token === generation.current) setError(e.message);
    } finally {
      if (token === generation.current) setLoading(false);
    }
  };
  useEffect(() => {
    load();
    return () => {
      generation.current += 1;
    };
  }, [query.q, query.status, query.offset]);

  const groups = useMemo(
    () =>
      Object.entries(
        versions.reduce((all, version) => {
          (all[version.scheme] ||= []).push(version);
          return all;
        }, {}),
      ).sort(([a], [b]) => a.localeCompare(b)),
    [versions],
  );

  async function createDraft(event) {
    event.preventDefault();
    if (createBusy) return;
    const token = generation.current;
    setCreateBusy(true);
    setCreateError("");
    try {
      const created = await frameworkApi.create({
        ...form,
        effective_from: form.effective_from || null,
      });
      if (token !== generation.current) return;
      setShowCreate(false);
      setForm({
        scheme: form.scheme,
        label: "",
        notes: "",
        effective_from: "",
      });
      navigate(`/admin/framework/${created.id}`);
    } catch (e) {
      setCreateError(e.message);
    } finally {
      setCreateBusy(false);
    }
  }
  async function clone(id) {
    if (cloneBusy) return;
    const token = generation.current;
    setCloneBusy(id);
    try {
      const created = await frameworkApi.clone(id);
      if (token !== generation.current) return;
      navigate(`/admin/framework/${created.id}`);
    } catch (e) {
      if (token === generation.current) setError(e.message);
    } finally {
      if (token === generation.current) setCloneBusy("");
    }
  }

  return (
    <section
      className="framework-panel max-w-6xl mx-auto w-full min-w-0 px-1 sm:px-0"
      aria-labelledby="framework-title"
    >
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1
            id="framework-title"
            className="text-3xl font-bold text-foreground flex items-center gap-2"
          >
            <BookOpen className="w-7 h-7 text-teal-700" aria-hidden="true" />
            Frameworks
          </h1>
          <p className="mt-1 text-muted-foreground">
            Versioned assessment content and publication controls.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            className="min-h-11 inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Refresh
          </button>
          <button
            type="button"
            ref={createTrigger}
            onClick={() => {
              setCreateError("");
              setShowCreate(true);
            }}
            className="min-h-11 inline-flex items-center gap-2 rounded-lg bg-teal-700 px-3 py-2 text-sm font-semibold text-white hover:bg-teal-800"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            New draft
          </button>
        </div>
      </div>
      <AdminListToolbar search={query.q || ""} searchLabel="Search framework directory" total={total} loading={loading} onSearch={(q) => update({ q })} onReset={reset}>
        <label className="text-sm font-medium">Status<select aria-label="Filter framework status" value={query.status || "all"} onChange={(event) => update({ status: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All statuses</option><option value="draft">Draft</option><option value="published">Published</option><option value="retired">Retired</option></select></label>
      </AdminListToolbar>
      {error && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
        >
          <AlertCircle className="mr-2 inline h-4 w-4" aria-hidden="true" />
          {error}
        </div>
      )}
      {loading ? (
        <div
          role="status"
          className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground"
        >
          Loading framework versions…
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <p className="font-semibold">No framework versions yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Create a draft to begin a versioned framework.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(([scheme, rows]) => {
            const current = currentFor(rows);
            return (
              <section
                key={scheme}
                aria-labelledby={`scheme-${scheme}`}
                className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted px-5 py-4">
                  <div>
                    <h2
                      id={`scheme-${scheme}`}
                      className="font-bold text-foreground"
                    >
                      {scheme}
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      {rows.length} version{rows.length === 1 ? "" : "s"}
                      {current
                        ? ` · current publication: v${current.version}`
                        : " · no current publication"}
                    </p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm">
                    <caption className="sr-only">
                      {scheme} framework versions
                    </caption>
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="px-5 py-3">Version</th>
                        <th>Label</th>
                        <th>Status</th>
                        <th>Effective from</th>
                        <th>Published</th>
                        <th className="px-5">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...rows]
                        .sort((a, b) => b.version - a.version)
                        .map((v) => (
                          <tr key={v.id} className="border-t border-border">
                            <td className="px-5 py-3 font-semibold">
                              v{v.version}
                            </td>
                            <td>{v.label}</td>
                            <td>
                              <span
                                className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${v.status === "published" ? "border-teal-200 bg-teal-50 text-teal-900" : v.status === "retired" ? "border-slate-300 bg-slate-100 text-slate-700" : "border-amber-200 bg-amber-50 text-amber-800"}`}
                              >
                                {v.status}
                              </span>
                            </td>
                            <td>{fmtDate(v.effective_from)}</td>
                            <td>{fmtDate(v.published_at)}</td>
                            <td className="px-5">
                              <div className="flex flex-wrap items-center justify-end gap-2">
                                <Link
                                  className="inline-flex min-h-9 items-center rounded-md border border-border px-2 text-xs font-semibold text-foreground hover:bg-muted"
                                  to={`/admin/framework/${v.id}`}
                                >
                                  View
                                </Link>
                                <button
                                  type="button"
                                  disabled={!!cloneBusy}
                                  onClick={() => clone(v.id)}
                                  className="inline-flex min-h-9 items-center gap-1 rounded-md border border-border px-2 text-xs font-semibold hover:bg-muted disabled:opacity-50"
                                >
                                  <Copy
                                    className="h-3 w-3"
                                    aria-hidden="true"
                                  />
                                  {cloneBusy === v.id ? "Cloning…" : "Clone"}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })}
        </div>
      )}
      {!loading && !error && <AdminListPager offset={Number(query.offset || 0)} limit={25} total={total} onPage={(offset) => update({ offset }, { resetOffset: false })} />}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent
          className="framework-panel"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            createTrigger.current?.focus();
          }}
        >
          <DialogHeader>
            <DialogTitle>Create framework draft</DialogTitle>
            <DialogDescription>
              Required fields are validated again by the server.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createDraft}>
            <div className="mt-5 space-y-4">
              <label className="block text-sm font-semibold">
                Scheme
                <input
                  value={form.scheme}
                  onChange={(e) => setForm({ ...form, scheme: e.target.value })}
                  required
                  pattern="[a-z][a-z0-9_-]{1,63}"
                  className="mt-1 min-h-11 w-full rounded-lg border border-border px-3"
                />
              </label>
              <label className="block text-sm font-semibold">
                Label
                <input
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  required
                  maxLength={200}
                  className="mt-1 min-h-11 w-full rounded-lg border border-border px-3"
                />
              </label>
              <label className="block text-sm font-semibold">
                Notes
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  maxLength={10000}
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-border p-3"
                />
              </label>
              <label className="block text-sm font-semibold">
                Effective from (optional)
                <input
                  type="date"
                  value={form.effective_from}
                  onChange={(e) =>
                    setForm({ ...form, effective_from: e.target.value })
                  }
                  className="mt-1 min-h-11 w-full rounded-lg border border-border px-3"
                />
              </label>
            </div>
            {createError && (
              <p role="alert" className="mt-3 text-sm text-red-700">
                {createError}
              </p>
            )}
            <DialogFooter className="mt-6">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="min-h-11 rounded-lg border border-border px-4 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                disabled={createBusy}
                type="submit"
                className="min-h-11 rounded-lg bg-teal-700 px-4 text-sm font-semibold text-white disabled:opacity-50"
              >
                {createBusy ? "Creating…" : "Create draft"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
